import { Fade, Popper } from '@mui/material'
import { memo, ReactNode, useEffect, useRef, useSyncExternalStore } from 'react'
import ReactDOM from 'react-dom'

const ANCHOR_OFFSET: [number, number] = [10, 0]

const subscribeFullscreen = (onStoreChange: () => void) => {
    document.addEventListener('fullscreenchange', onStoreChange)
    return () => document.removeEventListener('fullscreenchange', onStoreChange)
}

const getFullscreenElement = () => document.fullscreenElement as HTMLElement | null

const useFullscreenElement = () =>
    useSyncExternalStore(subscribeFullscreen, getFullscreenElement, () => null)

const StrikePopover = ({
    anchorEl,
    open,
    handleClose,
    children,
}: {
    anchorEl: HTMLElement | null
    open: boolean
    handleClose: () => void
    children: ReactNode
}) => {
    const paperRef = useRef<HTMLDivElement>(null)
    const fullscreenEl = useFullscreenElement()

    useEffect(() => {
        if (!open || !anchorEl) return

        const handlePointerDown = (event: MouseEvent) => {
            const target = event.target as Node
            if (anchorEl.contains(target)) return
            if (paperRef.current?.contains(target)) return
            handleClose()
        }

        const attachTimer = window.setTimeout(() => {
            document.addEventListener('mousedown', handlePointerDown, true)
        }, 0)

        return () => {
            window.clearTimeout(attachTimer)
            document.removeEventListener('mousedown', handlePointerDown, true)
        }
    }, [open, anchorEl, handleClose])

    if (!open || !anchorEl) return null

    // When in fullscreen, we need to render a fixed-position popup manually
    // because Popper portals to body which is not visible over native fullscreen
    if (fullscreenEl) {
        const rect = anchorEl.getBoundingClientRect()
        return ReactDOM.createPortal(
            <div
                ref={paperRef}
                style={{
                    position: 'fixed',
                    top: rect.top,
                    left: rect.right + ANCHOR_OFFSET[0],
                    zIndex: 99999,
                    pointerEvents: 'auto',
                }}
            >
                {children}
            </div>,
            fullscreenEl
        )
    }

    return (
        <Popper
            open={open}
            anchorEl={anchorEl}
            placement="right-start"
            transition
            modifiers={[{ name: 'offset', options: { offset: ANCHOR_OFFSET } }]}
            sx={{ zIndex: (theme) => theme.zIndex.modal, pointerEvents: 'none' }}
        >
            {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={150}>
                    <div ref={paperRef} style={{ pointerEvents: 'auto' }}>
                        {children}
                    </div>
                </Fade>
            )}
        </Popper>
    )
}

export default memo(StrikePopover)
